import { ChecklistFieldType } from '@cobliteam/api-lib';
import { useTranslation } from '@cobliteam/i18n';
import { Button } from '@cobliteam/ui/components';
import { FormControl, FormField, FormItem, FormMessage, Input, useFieldArray, useFormContext } from '@cobliteam/ui/form';

import Icon from '@cobliteam/ui/icons';
import { DraggableList } from '@cobliteam/ui-business/components';
import type { DragControls } from 'motion/react';
import { useEffect } from 'react';
import type { FormValues } from '../../checklistSchema';

interface ChecklistItemOptionsProps {
  index: number;
}

const ChecklistItemOptions = ({ index }: ChecklistItemOptionsProps) => {
  const { t } = useTranslation('checklists');
  const { control, watch } = useFormContext<FormValues>();
  const {
    fields: options,
    append: addOption,
    remove: removeOption,
    swap: swapOptions,
  } = useFieldArray({
    control,
    name: `items.${index}.content.options`,
  });

  const itemType = watch(`items.${index}.type`);
  const hasSelectOptions = itemType === ChecklistFieldType.SINGLE_SELECT || itemType === ChecklistFieldType.MULTI_SELECT;

  const handleReorder = (newOrder: typeof options) => {
    let swappedIndexes: [number, number] | null = null;
    newOrder.forEach((option, optionIndex) => {
      if (option.id !== options[optionIndex].id) {
        swappedIndexes = [optionIndex, options.findIndex((f) => f.id === option.id)];
      }
    });

    if (!swappedIndexes) return;
    swapOptions(swappedIndexes[0], swappedIndexes[1]);
  };

  useEffect(() => {
    if (hasSelectOptions && options.length === 0) {
      addOption({ label: '' });
    }
  }, [hasSelectOptions, options, addOption]);

  if (!hasSelectOptions) return null;

  return (
    <div className='space-y-2.5'>
      <DraggableList values={options} onReorder={handleReorder} className='flex flex-col gap-2.5'>
        {options.map((option, optionIndex) => (
          <DraggableList.Item
            key={option.id}
            value={option}
            style={{ borderRadius: '0.375rem' }}
            renderItem={(dragControls: DragControls) => (
              <FormField control={control} name={`items.${index}.content.options.${optionIndex}.label`}>
                {({ field, formState }) => (
                  <div>
                    <div className='flex items-center gap-1'>
                      <Button
                        size='sm'
                        variant='ghost'
                        className='shrink-0 cursor-grab active:cursor-grabbing'
                        icon={<Icon name='grip-vertical' color='secondary' />}
                        onPointerDown={(e) => dragControls.start(e)}
                      />
                      <FormItem className='w-full'>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={t('option_number', { number: optionIndex + 1 })}
                            error={!!formState.errors.items?.[index]?.content?.options?.[optionIndex]?.label}
                            onKeyUp={(e) => {
                              if (e.key === 'Enter' || e.keyCode === 13) addOption({ label: '' });
                            }}
                          />
                        </FormControl>
                      </FormItem>
                      {options.length > 1 && <Button variant='ghost' color='default' size='sm' icon={<Icon name='x' />} onClick={() => removeOption(optionIndex)} />}
                    </div>
                    {formState.errors.items?.[index]?.content?.options?.[optionIndex]?.label && <FormMessage />}
                  </div>
                )}
              </FormField>
            )}
          />
        ))}
      </DraggableList>
      <Button variant='outlined' color='default' size='sm' startIcon={<Icon name='plus' />} onClick={() => addOption({ label: '' })}>
        {t('option')}
      </Button>
    </div>
  );
};

export default ChecklistItemOptions;
